export type SurfaceVec2 = readonly [number, number];

export type SurfaceCutterProfile =
  | { kind: "prism" }
  | {
      kind: "linearRamp";
      /** Unit direction in cutter-local XY from the base toward the precipice. */
      axis: readonly [number, number];
      /** Coordinate along `axis` where the ramp starts at base height. */
      base: number;
      /** Coordinate along `axis` where the ramp reaches full height. */
      lead: number;
    };

export type SurfaceProfileTarget =
  | { kind: "face" }
  | { kind: "edge"; edgeIndex: number };

export const SURFACE_PRISM_PROFILE: SurfaceCutterProfile = { kind: "prism" };
export const SURFACE_FACE_TARGET: SurfaceProfileTarget = { kind: "face" };

const EPSILON = 1e-9;

export function normalizeSurfaceProfile(
  profile: SurfaceCutterProfile | null | undefined,
): SurfaceCutterProfile {
  if (!profile || profile.kind === "prism") return SURFACE_PRISM_PROFILE;
  const ax = profile.axis[0];
  const ay = profile.axis[1];
  const len = Math.hypot(ax, ay);
  const span = profile.lead - profile.base;
  if (len < EPSILON || Math.abs(span) < EPSILON) return SURFACE_PRISM_PROFILE;
  return {
    kind: "linearRamp",
    axis: [ax / len, ay / len],
    base: profile.base,
    lead: profile.lead,
  };
}

export function surfaceProfileHeightT(
  profile: Extract<SurfaceCutterProfile, { kind: "linearRamp" }>,
  x: number,
  y: number,
): number {
  const span = profile.lead - profile.base;
  if (Math.abs(span) < EPSILON) return 1;
  const coord = x * profile.axis[0] + y * profile.axis[1];
  return Math.max(0, Math.min(1, (coord - profile.base) / span));
}

export function surfaceProfileRampZ(
  profile: Extract<SurfaceCutterProfile, { kind: "linearRamp" }>,
  x: number,
  y: number,
  height: number,
  reverse: boolean,
  cutStartOffset: number,
): number {
  const t = surfaceProfileHeightT(profile, x, y);
  return reverse ? cutStartOffset - (height + cutStartOffset) * t : height * t;
}

export function sampleSurfaceRectangleLoop(
  width: number,
  depth: number,
): SurfaceVec2[] {
  const hx = width * 0.5;
  const hy = depth * 0.5;
  // CCW from +X +Y when looking down +Z.
  return [
    [hx, hy],
    [-hx, hy],
    [-hx, -hy],
    [hx, -hy],
  ];
}

export function sampleSurfaceEllipseLoop(
  radiusX: number,
  radiusY: number,
  segments: number,
): SurfaceVec2[] {
  const n = Math.max(3, Math.round(segments));
  const out: SurfaceVec2[] = [];
  for (let i = 0; i < n; i++) {
    const theta = (2 * Math.PI * i) / n;
    out.push([Math.cos(theta) * radiusX, Math.sin(theta) * radiusY]);
  }
  return out;
}

export function surfaceProfileFromTarget(
  loop: ReadonlyArray<SurfaceVec2>,
  target: SurfaceProfileTarget | null | undefined,
): SurfaceCutterProfile {
  if (!target || target.kind === "face") return SURFACE_PRISM_PROFILE;
  return linearRampProfileFromLoopEdge(loop, target.edgeIndex);
}

export function linearRampProfileFromLoopEdge(
  loop: ReadonlyArray<SurfaceVec2>,
  edgeIndex: number,
): SurfaceCutterProfile {
  const n = loop.length;
  if (n < 3) return SURFACE_PRISM_PROFILE;
  const i0 = positiveModulo(Math.round(edgeIndex), n);
  const i1 = (i0 + 1) % n;
  return linearRampProfileFromEdgePoints(loop, loop[i0]!, loop[i1]!);
}

export function linearRampProfileFromEdgePoints(
  loop: ReadonlyArray<SurfaceVec2>,
  edgeA: SurfaceVec2,
  edgeB: SurfaceVec2,
): SurfaceCutterProfile {
  if (loop.length < 3) return SURFACE_PRISM_PROFILE;

  const ex = edgeB[0] - edgeA[0];
  const ey = edgeB[1] - edgeA[1];
  const edgeLen = Math.hypot(ex, ey);
  if (edgeLen < EPSILON) return SURFACE_PRISM_PROFILE;

  let cx = 0;
  let cy = 0;
  for (const [x, y] of loop) {
    cx += x;
    cy += y;
  }
  cx /= loop.length;
  cy /= loop.length;

  const mx = (edgeA[0] + edgeB[0]) * 0.5;
  const my = (edgeA[1] + edgeB[1]) * 0.5;
  let ax = ey / edgeLen;
  let ay = -ex / edgeLen;
  if (ax * (mx - cx) + ay * (my - cy) < 0) {
    ax = -ax;
    ay = -ay;
  }

  const lead = mx * ax + my * ay;
  return linearRampProfileFromAxisAndLead(loop, [ax, ay], lead);
}

export function linearRampProfileFromEllipseEdge(
  radiusX: number,
  radiusY: number,
  segments: number,
  edgeIndex: number,
): SurfaceCutterProfile {
  const n = Math.max(3, Math.round(segments));
  const i0 = positiveModulo(Math.round(edgeIndex), n);
  const i1 = (i0 + 1) % n;
  const rx = Math.max(EPSILON, Math.abs(radiusX));
  const ry = Math.max(EPSILON, Math.abs(radiusY));
  const theta = ((i0 + 0.5) / n) * Math.PI * 2;
  const leadPoint: SurfaceVec2 = [Math.cos(theta) * rx, Math.sin(theta) * ry];
  const axis = ellipseNormalAxis(rx, ry, leadPoint);
  if (!axis) return SURFACE_PRISM_PROFILE;

  const loop = sampleSurfaceEllipseLoop(rx, ry, n);
  const edgeA = loop[i0]!;
  const edgeB = loop[i1]!;
  const edgeMid: SurfaceVec2 = [
    (edgeA[0] + edgeB[0]) * 0.5,
    (edgeA[1] + edgeB[1]) * 0.5,
  ];
  const lead = Math.max(
    edgeA[0] * axis[0] + edgeA[1] * axis[1],
    edgeB[0] * axis[0] + edgeB[1] * axis[1],
    edgeMid[0] * axis[0] + edgeMid[1] * axis[1],
  );
  return linearRampProfileFromAxisAndLead(loop, axis, lead);
}

export function linearRampProfileFromEllipsePoint(
  radiusX: number,
  radiusY: number,
  point: SurfaceVec2,
): SurfaceCutterProfile {
  const axis = ellipseNormalAxis(radiusX, radiusY, point);
  if (!axis) return SURFACE_PRISM_PROFILE;

  const rx = Math.max(EPSILON, Math.abs(radiusX));
  const ry = Math.max(EPSILON, Math.abs(radiusY));
  const support = Math.hypot(rx * axis[0], ry * axis[1]);
  if (support < EPSILON) return SURFACE_PRISM_PROFILE;
  return {
    kind: "linearRamp",
    axis,
    base: -support,
    lead: support,
  };
}

function ellipseNormalAxis(
  radiusX: number,
  radiusY: number,
  point: SurfaceVec2,
): readonly [number, number] | null {
  const rx = Math.max(EPSILON, Math.abs(radiusX));
  const ry = Math.max(EPSILON, Math.abs(radiusY));
  let ax = point[0] / (rx * rx);
  let ay = point[1] / (ry * ry);
  const len = Math.hypot(ax, ay);
  if (len < EPSILON) return null;
  ax /= len;
  ay /= len;
  return [ax, ay];
}

function linearRampProfileFromAxisAndLead(
  loop: ReadonlyArray<SurfaceVec2>,
  axis: readonly [number, number],
  lead: number,
): SurfaceCutterProfile {
  let base = Infinity;
  for (const [x, y] of loop) {
    base = Math.min(base, x * axis[0] + y * axis[1]);
  }
  if (!Number.isFinite(base) || Math.abs(lead - base) < EPSILON) {
    return SURFACE_PRISM_PROFILE;
  }
  return { kind: "linearRamp", axis, base, lead };
}

function positiveModulo(value: number, mod: number): number {
  return ((value % mod) + mod) % mod;
}
